import { useMemo, useState } from 'react'
import { radius, theme } from '../theme'
import { Card } from './ui'

/**
 * Every word the child has, as a field of rice (Sam, 22 Oct 2026).
 *
 * The tree counts only fully grown words, and the garden counts every word
 * met, so the two numbers never matched and nothing on screen said why. Here
 * all the words stand in one field cut into three plots, one grain per word:
 * orange is ripe (grown, and growing the tree), yellow is still growing,
 * green is newly planted. Each plot is as wide as its share of the words, so
 * the big orange plot and the tree's number are plainly the same thing.
 *
 * Tap a plot to walk into it, then tap (or point at) any grain to read its
 * word. Tapping is the point: an iPad has no hover.
 */

export interface Fields { ripe: string[]; growing: string[]; new: string[] }

type Kind = keyof Fields

const PLOTS: { kind: Kind; label: string; color: string; dark: string; line: string }[] = [
  { kind: 'ripe', label: 'Ripe', color: '#F29A2E', dark: '#B8661A',
    line: 'Fully grown. These grow your tree.' },
  { kind: 'growing', label: 'Growing', color: '#F2CF45', dark: '#B39220',
    line: 'Practised, not ripe yet.' },
  { kind: 'new', label: 'New', color: '#69BF4A', dark: '#3E8A2A',
    line: 'Planted, waiting for first practice.' },
]

/** A grid of grains filling a w by h box, one per word, as evenly as fits. */
function grains(n: number, w: number, h: number): [number, number, number][] {
  if (n <= 0) return []
  const cols = Math.max(1, Math.round(Math.sqrt((n * w) / h)))
  const rows = Math.ceil(n / cols)
  const dx = w / cols
  const dy = h / rows
  const r = Math.max(0.9, Math.min(dx, dy) * 0.34)
  const out: [number, number, number][] = []
  for (let i = 0; i < n; i++) {
    const c = i % cols
    const row = Math.floor(i / cols)
    out.push([dx * (c + 0.5), dy * (row + 0.5), r])
  }
  return out
}

export function WordField({ fields }: { fields: Fields }) {
  const [open, setOpen] = useState<Kind | null>(null)
  const counts = PLOTS.map((p) => fields[p.kind].length)
  const total = counts.reduce((a, b) => a + b, 0)

  // Plot widths follow the words, with a floor so a small plot can be tapped.
  const W = 320
  const H = 120
  const gap = 6
  const widths = useMemo(() => {
    const usable = W - gap * (PLOTS.length - 1)
    const floor = 44
    const raw = counts.map((c) => (total ? (c / total) * usable : usable / PLOTS.length))
    const lifted = raw.map((x) => Math.max(floor, x))
    const over = lifted.reduce((a, b) => a + b, 0) - usable
    const big = lifted.map((x) => (x > floor ? x : 0)).reduce((a, b) => a + b, 0) || 1
    return lifted.map((x) => (x > floor ? x - (over * x) / big : x))
  }, [counts.join(','), total])

  if (!total) return null

  if (open) {
    return <Plot kind={open} words={fields[open]} onBack={() => setOpen(null)} />
  }

  let x = 0
  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 800, color: theme.text, marginBottom: 4 }}>
        🌾 Your word field · {total.toLocaleString()} words
      </div>
      <div style={{ fontSize: 14, color: theme.muted, marginBottom: 10, lineHeight: 1.45 }}>
        A word is <b>ripe</b> once you have got it right in all 3 kinds of question
        (knowing it, spelling it, using it), in at least 3 different practices. Only ripe
        words grow your tree. Tap a plot to look inside.
      </div>
      <svg viewBox={`0 0 ${W} ${H + 4}`} role="img" aria-label="Your words as a field"
        style={{ width: '100%', display: 'block' }}>
        {PLOTS.map((p, i) => {
          const w = widths[i]!
          const x0 = x
          x += w + gap
          const dots = grains(counts[i]!, w - 8, H - 8)
          return (
            <g key={p.kind} transform={`translate(${x0} 0)`} style={{ cursor: 'pointer' }}
              onClick={() => setOpen(p.kind)} role="button" aria-label={`${p.label}: ${counts[i]} words`}>
              <rect x={0} y={0} width={w} height={H} rx={8} fill="#8A6A3E" />
              <rect x={2} y={2} width={w - 4} height={H - 4} rx={7} fill={p.color} opacity={0.22} />
              <g transform="translate(4 4)">
                {dots.map(([cx, cy, r], k) => <circle key={k} cx={cx} cy={cy} r={r} fill={p.color} />)}
              </g>
            </g>
          )
        })}
      </svg>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {PLOTS.map((p, i) => (
          <button key={p.kind} type="button" onClick={() => setOpen(p.kind)} style={{
            flex: '1 1 120px', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer',
            background: '#FFFFFF', border: `2px solid ${p.color}`, borderRadius: radius.md,
            padding: '8px 10px',
          }}>
            <div style={{ fontWeight: 800, color: p.dark, fontSize: 16 }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5,
                background: p.color, marginRight: 6 }} />
              {p.label} · {counts[i]!.toLocaleString()}
            </div>
            <div style={{ fontSize: 13, color: theme.muted, marginTop: 2 }}>{p.line}</div>
          </button>
        ))}
      </div>
    </Card>
  )
}

/** Inside one plot: a grain per word, big enough to tap. */
function Plot({ kind, words, onBack }: { kind: Kind; words: string[]; onBack: () => void }) {
  const p = PLOTS.find((q) => q.kind === kind)!
  const [picked, setPicked] = useState<string | null>(null)
  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ fontWeight: 800, color: p.dark, fontSize: 17 }}>
          {p.label} · {words.length.toLocaleString()} words
        </div>
        <button type="button" onClick={onBack} style={{
          fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: theme.text2, cursor: 'pointer',
          background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: radius.sm, padding: '6px 12px',
        }}>← Whole field</button>
      </div>
      <div style={{ fontSize: 14, color: theme.muted, margin: '4px 0 10px' }}>{p.line}</div>
      <div role="status" style={{
        minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Fredoka', sans-serif", fontSize: 24, fontWeight: 600, color: p.dark,
        background: theme.bg, borderRadius: radius.md, marginBottom: 10,
      }}>
        {picked ?? 'Tap a grain to see its word'}
      </div>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 5, maxHeight: 360, overflowY: 'auto',
        padding: 8, background: '#F3E7D2', borderRadius: radius.md,
      }}>
        {words.map((w, i) => (
          <button key={`${w}-${i}`} type="button" title={w} aria-label={w}
            onClick={() => setPicked(w)} onMouseEnter={() => setPicked(w)} style={{
              width: 22, height: 22, padding: 0, cursor: 'pointer', borderRadius: 11,
              background: p.color, border: `2px solid ${picked === w ? p.dark : 'transparent'}`,
              transform: picked === w ? 'scale(1.35)' : 'none', transition: 'transform 120ms',
            }} />
        ))}
      </div>
    </Card>
  )
}
